import { useEffect, useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Label } from '@verlo/shared/components/ui/label';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import { Loader2, MessageSquare } from 'lucide-react';

/**
 * A message for the table (30 Sep 2026).
 *
 * Three registers, because they do not behave the same on paper: an **allergy** prints
 * loudest and in red where there is a ribbon; a **kitchen** instruction prints and shows on
 * the board; a **house** note never leaves the floor, because a chef reading "paying
 * separately" learns nothing and the paper is finite.
 *
 * No manager is asked. A message moves no money, and fetching one to say "no onions" is how
 * a kitchen ends up being told nothing at all.
 */
export const MESSAGE_KINDS = [
  { value: 'kitchen', label: 'Kitchen', hint: 'Prints on the ticket and shows on the board.' },
  { value: 'allergy', label: 'Allergy', hint: 'Prints loudest, and in red where the printer has a ribbon.' },
  { value: 'house', label: 'House', hint: 'For the floor only. The kitchen never sees it.' },
];

const PLACEHOLDER = {
  kitchen: 'Mains together, hold the dessert',
  allergy: 'Nut allergy on this table',
  house: 'Paying separately',
};

const MessageDialog = ({ open, onOpenChange, onSubmit, started = false }) => {
  const [kind, setKind] = useState('kitchen');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setKind('kitchen');
    setText('');
    setError('');
  }, [open]);

  const submit = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit({ kind, text: text.trim() });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send the message');
      setSaving(false);
      return;
    }
    setSaving(false);
    onOpenChange(false);
  };

  const hint = MESSAGE_KINDS.find((k) => k.value === kind)?.hint;

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent size="confirm" data-testid="message-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-verlo">
            <MessageSquare size={18} />
            <DialogTitle>Message for this table</DialogTitle>
          </div>
          <DialogDescription>
            {started
              ? 'The kitchen has already started this food, so this prints beside the card they are working from.'
              : 'It goes on every ticket this order sends to the kitchen.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Who it is for</Label>
            <Segmented
              value={kind}
              onChange={setKind}
              options={MESSAGE_KINDS.map((k) => ({ value: k.value, label: k.label, testId: `message-kind-${k.value}` }))}
              stretch
            />
            <p className="text-[11.5px] text-ink-faint">{hint}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Message</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={PLACEHOLDER[kind]}
              className="h-20"
              maxLength={280}
              data-testid="message-text"
              autoFocus
            />
          </div>
          {error && <p className="text-xs text-signal-stop text-center" data-testid="message-error">{error}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            className={kind === 'allergy' ? 'bg-signal-stop hover:bg-signal-stop gap-1.5' : 'bg-verlo hover:bg-verlo-hover gap-1.5'}
            onClick={submit}
            disabled={saving || !text.trim()}
            data-testid="message-send"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {kind === 'house' ? 'Add note' : 'Tell the kitchen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MessageDialog;
